/**
 * Guest-facing sharing helpers for the public gallery.
 *
 * At an event, guests overwhelmingly want a photo *in* Instagram/WhatsApp, not
 * a file sitting in their Downloads folder. The Web Share API opens the phone's
 * native share sheet with the actual image attached, which is both what they
 * want and more reliable than <a download>, that attribute is widely ignored
 * by iOS Safari, where it tends to just navigate to the image instead.
 */

/** Can this browser share actual image files (not just a URL)? */
export function canShareFiles(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.canShare && !!navigator.share
}

async function urlToFile(url: string, filename: string): Promise<File> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Could not load image (${res.status})`)
  const blob = await res.blob()
  return new File([blob], filename, { type: blob.type || 'image/jpeg' })
}

export type ShareResult = 'shared' | 'cancelled' | 'unsupported' | 'failed'

/**
 * Open the native share sheet with a single image.
 * Returns 'unsupported' when the caller should fall back to a plain download.
 */
export async function sharePhoto(url: string, filename: string, title?: string): Promise<ShareResult> {
  if (!canShareFiles()) return 'unsupported'

  try {
    const file = await urlToFile(url, filename)

    // canShare must be checked with the actual file, some browsers expose the
    // API but refuse specific types.
    if (!navigator.canShare?.({ files: [file] })) return 'unsupported'

    await navigator.share({ files: [file], title })
    return 'shared'
  } catch (err) {
    // The user dismissing the sheet throws AbortError; that's not an error.
    if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled'
    console.error('Share failed:', err)
    return 'failed'
  }
}

/**
 * Save an image to the device.
 *
 * Fetches to a blob first so the download attribute is honoured, pointing an
 * <a download> straight at the server URL opens the image in a new tab on
 * several mobile browsers rather than saving it.
 */
export async function savePhoto(url: string, filename: string): Promise<boolean> {
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    // Revoke on the next tick, revoking immediately can cancel the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
    return true
  } catch (err) {
    console.error('Save failed:', err)
    // Last resort: let the browser handle it however it can.
    window.open(url, '_blank', 'noopener')
    return false
  }
}

/** Build a friendly filename like "garden-of-tomorrow-photo-3.jpg". */
export function buildFilename(eventName: string | null, url: string, index?: number): string {
  const base = (eventName || 'photobooth').replace(/\s+/g, '-').toLowerCase()
  const ext = url.split('.').pop()?.split('?')[0] || 'jpg'
  return index != null ? `${base}-photo-${index}.${ext}` : `${base}.${ext}`
}
